/**
 * IGRA-110 — состояние раздела «Поделиться подпиской» (`GET /vpn/pool` и
 * действия владельца). Устроено как `devicesStore`/`circleStore`: плоский
 * options-стор, флаги загрузки и ошибок, защита от повторного сабмита.
 *
 * Состав пула локально НЕ правится: после приглашения, отзыва и исключения
 * ответ перечитывается целиком. Кто в пуле и сколько устройств свободно,
 * знает бэкенд — фронт не имеет права показывать это по памяти.
 */
import { defineStore } from 'pinia'
import {
  getPool,
  invite as inviteRequest,
  kick as kickRequest,
  leave as leaveRequest,
  revokeInvite as revokeInviteRequest,
} from '../api/poolApi'
import { poolErrorCode } from '../utils/poolInvite'
import type { VpnPoolCreatedInvite, VpnPoolResponse } from '../types/pool'

interface PoolState {
  pool: VpnPoolResponse | null
  loading: boolean
  /** Хотя бы один ответ получен — по нему экран отличает скелет от пустоты. */
  hasFetchedOnce: boolean
  error: string | null
  inviting: boolean
  inviteError: string | null
  /** Последнее созданное приглашение — им живёт окно «отправлено» и шит. */
  lastInvite: VpnPoolCreatedInvite | null
  /** Ушло ли письмо: у приглашения ссылкой — нет. */
  lastInviteEmailSent: boolean
  revokingId: number | null
  revokeError: string | null
  kickingUserId: number | null
  kickError: string | null
  /** Идёт выход из пула (IGRA-110 экран 09). */
  leaving: boolean
  leaveError: string | null
}

/** Коды §8 → строки экрана. Незнакомый код падает в общую строку. */
const INVITE_ERROR_KEYS: Record<string, string> = {
  VPN_POOL_TRIAL_ONLY: 'vpn.pool.invite.errors.trialOnly',
  VPN_POOL_NOT_OWNER: 'vpn.pool.invite.errors.notOwner',
  VPN_POOL_SEATS_FULL: 'vpn.pool.invite.errors.seatsFull',
  VPN_POOL_NO_FREE_DEVICE: 'vpn.pool.invite.errors.noFreeDevice',
  VPN_POOL_INVITE_EXISTS: 'vpn.pool.invite.errors.inviteExists',
  VPN_POOL_EMAIL_IS_MEMBER: 'vpn.pool.invite.errors.emailIsMember',
  VPN_POOL_EMAIL_INVALID: 'vpn.pool.invite.errors.emailInvalid',
  VPN_POOL_EMAIL_LIMIT: 'vpn.pool.invite.errors.emailLimit',
}

export const usePoolStore = defineStore('vpnPool', {
  state: (): PoolState => ({
    pool: null,
    loading: false,
    hasFetchedOnce: false,
    error: null,
    inviting: false,
    inviteError: null,
    lastInvite: null,
    lastInviteEmailSent: false,
    revokingId: null,
    revokeError: null,
    kickingUserId: null,
    kickError: null,
    leaving: false,
    leaveError: null,
  }),

  actions: {
    async fetchPool() {
      if (this.loading) return

      this.loading = true
      this.error = null

      try {
        this.pool = await getPool()
      } catch (error) {
        console.warn('[igra.now][vpn] Failed to load pool', error)
        this.error = 'vpn.pool.page.loadError'
      } finally {
        this.loading = false
        this.hasFetchedOnce = true
      }
    },

    /**
     * `email` не задан — приглашение ссылкой. Успех перечитывает пул: новое
     * приглашение обязано появиться в списке без перезагрузки страницы.
     * Возвращает созданное приглашение, чтобы экран открыл окно «отправлено».
     */
    async invite(email?: string): Promise<VpnPoolCreatedInvite | undefined> {
      if (this.inviting) return undefined

      this.inviting = true
      this.inviteError = null

      try {
        const result = await inviteRequest(email)
        this.lastInvite = result.invite
        this.lastInviteEmailSent = result.email_sent
        await this.fetchPool()
        return result.invite
      } catch (error) {
        console.warn('[igra.now][vpn] Failed to create pool invite', error)
        const code = poolErrorCode(error)
        this.inviteError = (code && INVITE_ERROR_KEYS[code]) || 'vpn.pool.invite.errors.generic'
        return undefined
      } finally {
        this.inviting = false
      }
    },

    async revokeInvite(id: number): Promise<boolean> {
      if (this.revokingId !== null) return false

      this.revokingId = id
      this.revokeError = null

      try {
        await revokeInviteRequest(id)
        await this.fetchPool()
        return true
      } catch (error) {
        console.warn('[igra.now][vpn] Failed to revoke pool invite', error)
        this.revokeError = 'vpn.pool.invite.errors.revoke'
        return false
      } finally {
        this.revokingId = null
      }
    },

    /**
     * Выход участника из общей подписки (R19). Состав пула локально не
     * правится и здесь: экран после успеха перечитывает `GET /vpn/overview` —
     * доступ у человека пропал, и знать об этом должен бэкенд, а не память
     * страницы.
     */
    async leave(): Promise<boolean> {
      if (this.leaving) return false

      this.leaving = true
      this.leaveError = null

      try {
        await leaveRequest()
        return true
      } catch (error) {
        console.warn('[igra.now][vpn] Failed to leave pool', error)
        this.leaveError = 'vpn.pool.leave.error'
        return false
      } finally {
        this.leaving = false
      }
    },

    async kick(userId: number): Promise<boolean> {
      if (this.kickingUserId !== null) return false

      this.kickingUserId = userId
      this.kickError = null

      try {
        await kickRequest(userId)
        await this.fetchPool()
        return true
      } catch (error) {
        console.warn('[igra.now][vpn] Failed to kick pool member', error)
        this.kickError = 'vpn.pool.members.kickError'
        return false
      } finally {
        this.kickingUserId = null
      }
    },
  },
})
